import React, { useCallback, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortDirection, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';

import { ListVoucherLogsQuery, ListVoucherLogsResponse } from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import CopyableUserLink from '../Common/CopyableUserLink';

type VoucherLog = ListVoucherLogsResponse['result']['logs'][number];

type Props = {
  voucherId?: string;
  queryOptions?: ListVoucherLogsQuery;
  onQueryOptionsChange?: (queryOptions: ListVoucherLogsQuery) => void;
  pageSizeOptions?: number[];
};

const DEFAULT_QUERY_OPTIONS: ListVoucherLogsQuery = {
  page: 0,
  limit: 20,
  sortBy: 'created_at',
  sortByDirection: 'desc',
};

const SORT_FIELD_TO_API_SORT_BY: Partial<Record<string, NonNullable<ListVoucherLogsQuery['sortBy']>>> = {
  createdAt: 'created_at',
  amount: 'amount',
  fkOrder: 'id_order',
};

const API_SORT_BY_TO_SORT_FIELD: Partial<Record<NonNullable<ListVoucherLogsQuery['sortBy']>, string>> = {
  created_at: 'createdAt',
  amount: 'amount',
  id_order: 'fkOrder',
};

const columns: Array<GridColDef<VoucherLog>> = [
  {
    field: 'fkOrder',
    headerName: 'Order ID',
    minWidth: 220,
    flex: 1,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.row.fkOrder}</Box>
        <Tooltip title="Open order">
          <IconButton
            component={Link}
            to={`/purchases/${params.row.fkOrder}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
          >
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
  { field: 'currencyCode', headerName: 'Currency', minWidth: 120, flex: 0.5 },
  { field: 'amount', headerName: 'Amount', minWidth: 130, flex: 0.5 },
  {
    field: 'createdBy',
    headerName: 'Created By',
    minWidth: 260,
    flex: 1,
    renderCell: (params) => <CopyableUserLink label={params.row.createdBy} userId={params.row.createdBy} />,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    minWidth: 180,
    flex: 0.8,
    renderCell: (params) => DateFormatterDetailed({ date: params.value }),
  },
  {
    field: 'deletedAt',
    headerName: 'Deleted At',
    minWidth: 180,
    flex: 0.8,
    renderCell: (params) => (params.value ? DateFormatterDetailed({ date: params.value }) : '-'),
  },
  { field: 'voucherLogId', headerName: 'Log ID', minWidth: 260, flex: 1 },
  { field: 'voucherId', headerName: 'Voucher ID', minWidth: 260, flex: 1 },
  { field: 'voucherUserId', headerName: 'Voucher User ID', minWidth: 260, flex: 1 },
];

export default function VoucherLogsTable({
  voucherId,
  queryOptions,
  onQueryOptionsChange,
  pageSizeOptions = [20, 50, 100],
}: Props) {
  const [internalQueryOptions, setInternalQueryOptions] = useState<ListVoucherLogsQuery>(DEFAULT_QUERY_OPTIONS);
  const isControlled = queryOptions !== undefined;

  const resolvedQueryOptions = useMemo(() => {
    return {
      ...DEFAULT_QUERY_OPTIONS,
      ...(isControlled ? queryOptions : internalQueryOptions),
      ...(voucherId ? { voucherId } : {}),
    };
  }, [internalQueryOptions, isControlled, queryOptions, voucherId]);

  const updateQueryOptions = useCallback(
    (updater: (current: ListVoucherLogsQuery) => ListVoucherLogsQuery) => {
      const current = isControlled
        ? {
            ...DEFAULT_QUERY_OPTIONS,
            ...queryOptions,
          }
        : internalQueryOptions;
      const next = updater(current);

      if (!isControlled) {
        setInternalQueryOptions(next);
      }
      onQueryOptionsChange?.(next);
    },
    [internalQueryOptions, isControlled, onQueryOptionsChange, queryOptions],
  );

  const { data, isLoading } = useListVoucherLogs({
    query: resolvedQueryOptions,
  });

  const rows = data?.result?.logs ?? [];
  const total = Number(data?.result?.total ?? 0);

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: resolvedQueryOptions.page ?? DEFAULT_QUERY_OPTIONS.page ?? 0,
      pageSize: resolvedQueryOptions.limit ?? DEFAULT_QUERY_OPTIONS.limit ?? 20,
    }),
    [resolvedQueryOptions.limit, resolvedQueryOptions.page],
  );

  const sortModel = useMemo<GridSortModel>(() => {
    if (!resolvedQueryOptions.sortBy) {
      return [];
    }
    const sortField = API_SORT_BY_TO_SORT_FIELD[resolvedQueryOptions.sortBy];
    if (!sortField) {
      return [];
    }

    return [
      {
        field: sortField,
        sort: (resolvedQueryOptions.sortByDirection ?? 'asc') as GridSortDirection,
      },
    ];
  }, [resolvedQueryOptions.sortBy, resolvedQueryOptions.sortByDirection]);

  const handlePaginationModelChange = useCallback(
    (nextModel: GridPaginationModel) => {
      updateQueryOptions((current) => ({
        ...current,
        page: nextModel.page,
        limit: nextModel.pageSize,
      }));
    },
    [updateQueryOptions],
  );

  const handleSortModelChange = useCallback(
    (nextSortModel: GridSortModel) => {
      const nextSort = nextSortModel[0];
      if (!nextSort || !nextSort.sort) {
        updateQueryOptions((current) => ({
          ...current,
          sortBy: undefined,
          sortByDirection: undefined,
        }));
        return;
      }

      const nextSortBy = SORT_FIELD_TO_API_SORT_BY[nextSort.field];
      if (!nextSortBy) {
        return;
      }

      updateQueryOptions((current) => ({
        ...current,
        sortBy: nextSortBy,
        sortByDirection: nextSort.sort === 'desc' ? 'desc' : 'asc',
      }));
    },
    [updateQueryOptions],
  );

  return (
    <Box mt={3}>
      <PageSubheader title={`Voucher Logs (${total})`} />
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row) => row.voucherLogId}
        loading={isLoading}
        rowCount={total}
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={pageSizeOptions}
        slots={{ pagination: GridPagination }}
        disableRowSelectionOnClick
      />
    </Box>
  );
}
